import React, { useState, useEffect, useContext } from "react";
import UserContext from '../dataContext'
import { BsPersonCheckFill } from "react-icons/bs";
import { slugify } from "../utils/functions";
import Spinner from "../Components/Spinner";

export default function Archives() {
    const userContext = useContext(UserContext);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [archivedData, setArchivedData] = useState({
        clients: [],
        concierges: [],
        prestataires: [],
        properties: []
    });

    // Fetch archived people on mount
    useEffect(() => {
        setLoading(true); // Start loading
        Promise.all([
            userContext.apiReducer('getArchivedPeople'),
            userContext.apiReducer('getArchivedProperties'),
        ])
            .then(([peopleData, propertiesData]) => {
                // Sort data after fetching
                const sortedClients = peopleData.Client?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                const sortedConcierges = peopleData.Concierge?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                const sortedPrestataires = peopleData.Prestataires?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];

                const rawProperties = propertiesData.Properties || []
                const sortedProperties = rawProperties.sort((a, b) => {
                    const aLast = a?.client?.lastName || ''
                    const bLast = b?.client?.lastName || ''
                    if (aLast !== bLast) return aLast > bLast ? 1 : -1
                    const aName = a?.property?.name || ''
                    const bName = b?.property?.name || ''
                    return aName > bName ? 1 : -1
                })

                setArchivedData({
                    clients: sortedClients,
                    concierges: sortedConcierges,
                    prestataires: sortedPrestataires,
                    properties: sortedProperties
                });
                setLoading(false); // Stop loading on success
            })
            .catch(error => {
                console.error("Error fetching archived people:", error);
                // Optionally set an error state here
                userContext.setError({
                    level: 'error',
                    message: 'Erreur lors de la récupération des archives.',
                    btn1Text: 'OK',
                    btn2hidden: true
                });
                setLoading(false); // Stop loading on error
            });
    }, [userContext.apiReducer, userContext.setError]); // Dependencies: apiReducer and setError

    if (loading) { // Only use the local loading state now
        return (
            <div className={"col border cardsContainer mx-1 p-2 mt-3 mt-md-0"}>
                <div className={"row justify-content-between"}>
                    <div className={"col-auto"}>
                        <h1>Archives</h1>
                    </div>
                </div>
                <div className="text-center my-5">
                    <Spinner strokeWidth={9} width={40} color={"#003952"} />
                    <p className="mt-3">Chargement des archives...</p>
                </div>
            </div>
        );
    }

    const { clients, concierges, prestataires, properties } = archivedData;
    // Filter based on the locally fetched archived data
    const filteredClients = clients.filter(client =>
        search === "" ||
        slugify(client.lastName).includes(slugify(search)) ||
        slugify(client.name).includes(slugify(search))
    );

    const filteredConcierges = concierges.filter(concierge =>
        search === "" ||
        slugify(concierge.lastName).includes(slugify(search)) ||
        slugify(concierge.name).includes(slugify(search))
    );

    const filteredPrestataires = prestataires.filter(prestataire =>
        search === "" ||
        slugify(prestataire.lastName)?.includes(slugify(search)) ||
        slugify(prestataire.name)?.includes(slugify(search))
    );

    const filteredProperties = properties.filter(p => {
        if (search === '') return true
        const clientLast = slugify(p?.client?.lastName || '')
        const clientName = slugify(p?.client?.name || p?.client?.email || '')
        const propertyName = slugify(p?.property?.name || '')
        const city = slugify(p?.property?.address?.city || '')
        const zip = slugify(p?.property?.address?.zip || '')
        return (
            clientLast.includes(slugify(search)) ||
            clientName.includes(slugify(search)) ||
            propertyName.includes(slugify(search)) ||
            city.includes(slugify(search)) ||
            zip.includes(slugify(search))
        )
    })

    return (
        <>
            <div className={"col border cardsContainer mx-1 p-2 mt-3 mt-md-0"}>
                <div className={"row justify-content-between"}>
                    <div>
                        <h1>Archives</h1>
                        <br></br>
                        <h2 className="text-center">Filtrer dans les profils</h2>
                        <input type={"search"} id={"form1"}
                            className={"form-control w-48 mx-auto"} placeholder={"Rechercher parmis les profils"}
                            name={"search"}
                            autoComplete={"off"}
                            onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>
            </div>
            <div className="d-flex flex-wrap justify-content-around">
                {/* Client Table */}
                <div className={"w-48 border cardsContainer mx-1 p-2 mt-5"}>
                    <div className={"row justify-content-between"}>
                        <div className={"col-auto"}>
                            <h1>Clients archivés</h1>
                        </div>
                    </div>
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Prénom</th>
                                <th>Téléphone</th>
                                <th>Email</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredClients.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center">Aucun client archivé trouvé</td>
                                </tr>
                            ) : (
                                filteredClients.map((client, index) => (
                                    <tr key={client.email || index}> {/* Use email as key if available */}
                                        <td>{client.lastName}</td>
                                        <td>{client.name}</td>
                                        <td>{client.phone}</td>
                                        <td>{client.email}</td>
                                        <td><button className={'btn btn-outline-dark'} onClick={() => {
                                            userContext.setError({
                                                level: 'warning',
                                                message: `Êtes vous sûr de vouloir restaurer le profil de ${client.name} ${client.lastName} ?`,
                                                btn1Text: 'Annuler',
                                                btn2Text: 'Confirmer',
                                                btn2Action: () => {
                                                    userContext.apiReducer('modifyClient', { archive: false }, { email: client.email })
                                                        .then(() => {
                                                            // Refresh data after successful restore
                                                            setLoading(true); // Show spinner while refreshing
                                                            userContext.apiReducer('getArchivedPeople')
                                                                .then(data => {
                                                                    const sortedClients = data.Client?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                                                                    const sortedConcierges = data.Concierge?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                                                                    const sortedPrestataires = data.Prestataires?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                                                                    setArchivedData({
                                                                        clients: sortedClients,
                                                                        concierges: sortedConcierges,
                                                                        prestataires: sortedPrestataires
                                                                    });
                                                                    setLoading(false);
                                                                    userContext.setError({
                                                                        level: 'success', // Use success level
                                                                        message: `Le profil de ${client.name} ${client.lastName} a bien été restauré.`,
                                                                        btn1Text: "D'accord",
                                                                        btn2hidden: true
                                                                    });
                                                                })
                                                                .catch(err => {
                                                                    console.error("Error refreshing archived people:", err);
                                                                    setLoading(false);
                                                                    userContext.setError({
                                                                        level: 'error',
                                                                        message: 'Erreur lors de la mise à jour des archives après restauration.',
                                                                        btn1Text: 'OK',
                                                                        btn2hidden: true
                                                                    });
                                                                });
                                                        })
                                                        .catch(err => {
                                                            console.error("Error restoring client:", err);
                                                            userContext.setError({
                                                                level: 'error',
                                                                message: 'Erreur lors de la restauration du client.',
                                                                btn1Text: 'OK',
                                                                btn2hidden: true
                                                            });
                                                        });
                                                }
                                            })
                                        }}><BsPersonCheckFill /></button></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Concierge Table */}
                <div className={"w-48 border cardsContainer mx-1 p-2 mt-5"}>
                    <div className={"row justify-content-between"}>
                        <div className={"col-auto"}>
                            <h1>Concierges archivés</h1>
                        </div>
                    </div>
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Prénom</th>
                                <th>Téléphone</th>
                                <th>Email</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredConcierges.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center">Aucun concierge archivé trouvé</td>
                                </tr>
                            ) : (
                                filteredConcierges.map((concierge, index) => (
                                    <tr key={concierge.email || index}> {/* Use email as key */}
                                        <td>{concierge.lastName}</td>
                                        <td>{concierge.name}</td>
                                        <td>{concierge.phone}</td>
                                        <td>{concierge.email}</td>
                                        <td><button className={'btn btn-outline-dark'} onClick={() => {
                                            userContext.setError({
                                                level: 'warning',
                                                message: `Êtes vous sûr de vouloir restaurer le profil de ${concierge.name} ${concierge.lastName} ?`,
                                                btn1Text: 'Annuler',
                                                btn2Text: 'Confirmer',
                                                btn2Action: () => {
                                                    userContext.apiReducer('modifyConcierge', { archive: false }, { email: concierge.email })
                                                        .then(() => {
                                                            // Refresh data after successful restore
                                                            setLoading(true);
                                                            userContext.apiReducer('getArchivedPeople')
                                                                .then(data => {
                                                                    const sortedClients = data.Client?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                                                                    const sortedConcierges = data.Concierge?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                                                                    const sortedPrestataires = data.Prestataires?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                                                                    setArchivedData({
                                                                        clients: sortedClients,
                                                                        concierges: sortedConcierges,
                                                                        prestataires: sortedPrestataires
                                                                    });
                                                                    setLoading(false);
                                                                    userContext.setError({
                                                                        level: 'success',
                                                                        message: `Le profil de ${concierge.name} ${concierge.lastName} a bien été restauré.`,
                                                                        btn1Text: "D'accord",
                                                                        btn2hidden: true
                                                                    });
                                                                })
                                                                .catch(err => {
                                                                    console.error("Error refreshing archived people:", err);
                                                                    setLoading(false);
                                                                    userContext.setError({
                                                                        level: 'error',
                                                                        message: 'Erreur lors de la mise à jour des archives après restauration.',
                                                                        btn1Text: 'OK',
                                                                        btn2hidden: true
                                                                    });
                                                                });
                                                        })
                                                        .catch(err => {
                                                            console.error("Error restoring concierge:", err);
                                                            userContext.setError({
                                                                level: 'error',
                                                                message: 'Erreur lors de la restauration du concierge.',
                                                                btn1Text: 'OK',
                                                                btn2hidden: true
                                                            });
                                                        });
                                                }
                                            })
                                        }}><BsPersonCheckFill /></button></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Prestataire Table */}
                <div className={"w-48 border cardsContainer mx-1 p-2 mt-5"}>
                    <div className={"row justify-content-between"}>
                        <div className={"col-auto"}>
                            <h1>Prestataires archivés</h1>
                        </div>
                    </div>
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Prénom</th>
                                <th>Téléphone</th>
                                <th>Email</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPrestataires.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center">Aucun prestataire archivé trouvé</td>
                                </tr>
                            ) : (
                                filteredPrestataires.map((prestataire, index) => (
                                    <tr key={prestataire.email || index}> {/* Use email as key */}
                                        <td>{prestataire.lastName}</td>
                                        <td>{prestataire.name}</td>
                                        <td>{prestataire.phone}</td>
                                        <td>{prestataire.email}</td>
                                        <td><button className={'btn btn-outline-dark'} onClick={() => {
                                            userContext.setError({
                                                level: 'warning',
                                                message: `Êtes vous sûr de vouloir restaurer le profil de ${prestataire.name} ${prestataire.lastName} ?`,
                                                btn1Text: 'Annuler',
                                                btn2Text: 'Confirmer',
                                                btn2Action: () => {
                                                    userContext.apiReducer('modifyProvider', { archive: false }, { email: prestataire.email })
                                                        .then(() => {
                                                            // Refresh data after successful restore
                                                            setLoading(true);
                                                            userContext.apiReducer('getArchivedPeople')
                                                                .then(data => {
                                                                    const sortedClients = data.Client?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                                                                    const sortedConcierges = data.Concierge?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                                                                    const sortedPrestataires = data.Prestataires?.sort((a, b) => a.lastName > b.lastName ? 1 : -1) || [];
                                                                    setArchivedData({
                                                                        clients: sortedClients,
                                                                        concierges: sortedConcierges,
                                                                        prestataires: sortedPrestataires
                                                                    });
                                                                    setLoading(false);
                                                                    userContext.setError({
                                                                        level: 'success',
                                                                        message: `Le profil de ${prestataire.name} ${prestataire.lastName} a bien été restauré.`,
                                                                        btn1Text: "D'accord",
                                                                        btn2hidden: true
                                                                    });
                                                                })
                                                                .catch(err => {
                                                                    console.error("Error refreshing archived people:", err);
                                                                    setLoading(false);
                                                                    userContext.setError({
                                                                        level: 'error',
                                                                        message: 'Erreur lors de la mise à jour des archives après restauration.',
                                                                        btn1Text: 'OK',
                                                                        btn2hidden: true
                                                                    });
                                                                });
                                                        })
                                                        .catch(err => {
                                                            console.error("Error restoring prestataire:", err);
                                                            userContext.setError({
                                                                level: 'error',
                                                                message: 'Erreur lors de la restauration du prestataire.',
                                                                btn1Text: 'OK',
                                                                btn2hidden: true
                                                            });
                                                        });
                                                }
                                            })
                                        }}><BsPersonCheckFill /></button></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Property Table */}
                <div className={"w-48 border cardsContainer mx-1 p-2 mt-5"}>
                    <div className={"row justify-content-between"}>
                        <div className={"col-auto"}>
                            <h1>Maisons archivées</h1>
                        </div>
                    </div>
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Maison</th>
                                <th>Ville</th>
                                <th>Code postal</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProperties.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center">Aucune maison archivée trouvée</td>
                                </tr>
                            ) : (
                                filteredProperties.map((p, idx) => (
                                    <tr key={(p?.property?.id || p?.property?.name || idx) + (p?.client?.email || '')}>
                                        <td>{p?.client?.lastName} {p?.client?.name ? p?.client?.name[0] + '.' : ''}</td>
                                        <td>{p?.property?.name}</td>
                                        <td>{p?.property?.address?.city}</td>
                                        <td>{p?.property?.address?.zip}</td>
                                        <td>
                                            <button
                                                className={'btn btn-outline-dark'}
                                                onClick={() => {
                                                    userContext.setError({
                                                        level: 'warning',
                                                        message: `Êtes vous sûr de vouloir restaurer la maison "${p?.property?.name}" ?`,
                                                        btn1Text: 'Annuler',
                                                        btn2Text: 'Confirmer',
                                                        btn2Action: () => {
                                                            setLoading(true)
                                                            userContext.apiReducer('modifyProperty', { archived: false }, { name: p?.property?.name })
                                                                .then(() => {
                                                                    userContext.apiReducer('getArchivedProperties')
                                                                        .then(propertiesData => {
                                                                            const rawProperties = propertiesData.Properties || []
                                                                            const sortedProperties = rawProperties.sort((a, b) => {
                                                                                const aLast = a?.client?.lastName || ''
                                                                                const bLast = b?.client?.lastName || ''
                                                                                if (aLast !== bLast) return aLast > bLast ? 1 : -1
                                                                                const aName = a?.property?.name || ''
                                                                                const bName = b?.property?.name || ''
                                                                                return aName > bName ? 1 : -1
                                                                            })
                                                                            setArchivedData(prev => ({ ...prev, properties: sortedProperties }))
                                                                            setLoading(false)
                                                                            userContext.setError({
                                                                                level: 'success',
                                                                                message: `La maison "${p?.property?.name}" a bien été restaurée.`,
                                                                                btn1Text: "D'accord",
                                                                                btn2hidden: true
                                                                            })
                                                                        })
                                                                        .catch(err => {
                                                                            console.error("Error refreshing archived properties:", err)
                                                                            setLoading(false)
                                                                            userContext.setError({
                                                                                level: 'error',
                                                                                message: 'Erreur lors de la mise à jour des archives après restauration.',
                                                                                btn1Text: 'OK',
                                                                                btn2hidden: true
                                                                            })
                                                                        })
                                                                })
                                                                .catch(err => {
                                                                    console.error("Error restoring property:", err)
                                                                    setLoading(false)
                                                                    userContext.setError({
                                                                        level: 'error',
                                                                        message: 'Erreur lors de la restauration de la maison.',
                                                                        btn1Text: 'OK',
                                                                        btn2hidden: true
                                                                    })
                                                                })
                                                        }
                                                    })
                                                }}
                                            >
                                                <BsPersonCheckFill />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}